import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, BadgeCheck, Lock, Landmark } from 'lucide-react';
import { api } from '@/api/client';

interface Banking {
  accountHolderName?: string;
  accountNumber?: string;
  bankName?: string;
  branchName?: string;
  ifscCode?: string;
  cancelledChequeFile?: string;
  isVerified?: boolean;
}

/**
 * Payout bank account.
 *
 * `merchantView` is the merchant portal. There it is **write-once**: a merchant fills the account in
 * while none is on file, and once saved the tab becomes a read-only summary — this is where their COD
 * remittances land, so a change has to go through support rather than being self-service. Admin
 * (`merchantView=false`) can always edit, and is the only side that can tick Verified. The server
 * enforces both rules independently; this is only the matching UI.
 */
export function MerchantBankingTab({ merchantId, merchantView = false }: { merchantId: number; merchantView?: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['merchant-banking', merchantId],
    queryFn: async () => {
      try { return (await api.get<Banking>(`/merchants/${merchantId}/banking`)).data ?? ({} as Banking); }
      catch { return {} as Banking; }
    },
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<Banking>({ values: data ?? {} });

  // "On file" = the four fields the server insists on. A half-written row can't happen through this
  // form, but reading the same four the API validates keeps the two definitions from drifting.
  const onFile = !!(data?.accountHolderName && data?.accountNumber && data?.bankName && data?.ifscCode);
  const readOnly = merchantView && onFile;

  const save = useMutation({
    mutationFn: async (d: Banking) => {
      const payload: any = {
        accountHolderName: d.accountHolderName,
        accountNumber: d.accountNumber,
        bankName: d.bankName,
        branchName: d.branchName,
        ifscCode: d.ifscCode?.toUpperCase(),
        cancelledChequeFile: d.cancelledChequeFile,
      };
      // Verified is an internal assertion — never sent from the merchant portal (the server strips it
      // there too, but there is no reason to put it on the wire).
      if (!merchantView) payload.isVerified = !!d.isVerified;
      return (await api.put(`/merchants/${merchantId}/banking`, payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant-banking', merchantId] });
      toast.success(merchantView ? 'Bank details saved' : 'Banking saved');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to save banking'),
  });

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  // ── Merchant, account already on file → read-only summary ──
  if (readOnly) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
          <Lock size={15} className="shrink-0 mt-0.5 text-slate-400" />
          <div>
            <div className="text-slate-700 font-medium">Your bank account is on file</div>
            <div className="text-slate-500 text-xs mt-0.5">
              This is where your COD remittances are paid, so it can&apos;t be changed from here.
              Contact support to update it — a change of payout account is verified by our team first.
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg divide-y">
          <ReadRow label="Account Holder Name" value={data?.accountHolderName} />
          <ReadRow label="Account Number" value={maskAccount(data?.accountNumber)} mono />
          <ReadRow label="Bank Name" value={data?.bankName} />
          <ReadRow label="IFSC Code" value={data?.ifscCode} mono />
          <ReadRow label="Branch Name" value={data?.branchName} />
          <ReadRow
            label="Verification"
            value={data?.isVerified
              ? <span className="inline-flex items-center gap-1 text-emerald-700"><BadgeCheck size={14} /> Verified</span>
              : <span className="text-amber-700">Pending verification by our team</span>}
          />
        </div>
      </div>
    );
  }

  // ── No account yet (merchant), or admin editing ──
  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-5">
      {merchantView && (
        <div className="flex items-start gap-2 bg-primary/5 border border-primary/20 rounded-lg p-3 text-sm">
          <Landmark size={15} className="shrink-0 mt-0.5 text-primary" />
          <div>
            <div className="text-slate-700 font-medium">Add your bank account</div>
            <div className="text-slate-500 text-xs mt-0.5">
              Your COD remittances are paid into this account. Check it carefully — once saved it can
              only be changed by contacting support.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Field label="Account Holder Name *" error={errors.accountHolderName?.message}>
          <input {...register('accountHolderName', { required: 'Required' })} placeholder="As per bank record" className="input" />
        </Field>
        <Field label="Account Number *" error={errors.accountNumber?.message}>
          <input {...register('accountNumber', { required: 'Required' })} className="input font-mono" />
        </Field>
        <Field label="Bank Name *" error={errors.bankName?.message}>
          <input {...register('bankName', { required: 'Required' })} placeholder="HDFC Bank" className="input" />
        </Field>
        <Field label="IFSC Code *" error={errors.ifscCode?.message}>
          <input {...register('ifscCode', { required: 'Required', pattern: { value: /^[A-Z]{4}0[A-Z0-9]{6}$/i, message: 'Invalid IFSC (e.g. HDFC0001234)' } })} className="input uppercase font-mono" />
        </Field>
        <Field label="Branch Name">
          <input {...register('branchName')} placeholder="Andheri East" className="input" />
        </Field>
        <Field label="Cancelled Cheque URL">
          <input {...register('cancelledChequeFile')} placeholder="https://..." className="input" />
        </Field>
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <div className="flex items-center gap-3 text-sm">
          {!merchantView && (
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" {...register('isVerified')} className="rounded" />
              <span className="inline-flex items-center gap-1 text-slate-700"><BadgeCheck size={14} /> Mark as Verified (admin)</span>
            </label>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => reset(data ?? {})} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Reset</button>
          <button type="submit" disabled={save.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2 text-sm">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {merchantView ? 'Save Bank Details' : 'Save Banking'}
          </button>
        </div>
      </div>
    </form>
  );
}

/** Show only the last 4 digits — the full number is on file and doesn't need to be on screen. */
function maskAccount(acc?: string) {
  const s = String(acc ?? '').trim();
  if (s.length <= 4) return s || '—';
  return '••••••' + s.slice(-4);
}

function ReadRow({ label, value, mono }: { label: string; value?: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={`text-slate-800 text-right ${mono ? 'font-mono' : ''}`}>{value || '—'}</span>
    </div>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}
